import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { motion } from 'framer-motion';
import { Mail, Lock, ArrowRight, ArrowLeft, Loader2 } from 'lucide-react';
import { apiFetch } from '@/utils/apiFetch';
import { User } from '@/api/entities';
import { getTenantFromEmail, isPlatformAccessBlockedEmail } from '@/lib/tenantUtils';
import { NEOGTM_LOGO_PNG_URL } from '@/lib/brandAssets';

const API_BASE_URL = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
  ? 'https://api.neogtm.com'
  : 'https://api.neogtm.com';
const BLOCKED_ACCESS_MESSAGE = 'Access has been temporarily blocked by the administrator.';

export default function Auth() {
  const navigate = useNavigate();
  const location = useLocation();
  const [isLogin, setIsLogin] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Form state
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [companyName, setCompanyName] = useState('');

  // Platform auth uses full-page redirect (no popup). Gmail/Outlook OAuth redirect to
  // /AuthCallback in the same tab, which then navigates to /Home. No postMessage listener needed.

  // Redirect to Home if already authenticated
  /* ── ?next= — where to go after signing in ─────────────────────────────────────────────────
   *
   * The MCP connector sends a signed-out user here: it has no NEO session, and its own form only
   * knows email+password while most users sign in with Google or Microsoft. Rather than grow a
   * second credential surface on the API, the connector delegates to this page and asks to be
   * returned to afterwards.
   *
   * 🔴 VALIDATED, NOT TRUSTED. `next` arrives in a URL, so an unchecked redirect here is an open
   * redirect: a phishing link could send someone to app.neogtm.com/Auth, have them sign in for
   * real, and then bounce them to an attacker's page still believing they are in NEO. So it must
   * parse, be https, be on the API origin, AND be the authorize path — nothing else is a reason
   * for this page to hand control to another URL. Anything that fails goes to Home. */
  const nextUrl = React.useMemo(() => {
    const raw = new URLSearchParams(location.search).get('next');
    if (!raw) return null;
    try {
      const u = new URL(raw);
      const apiOrigin = new URL(API_BASE_URL).origin;
      const ok = u.protocol === 'https:' && u.origin === apiOrigin
        && u.pathname === '/mcp/oauth/authorize';
      if (!ok) { console.warn('[Auth] refusing an off-site next=', raw); return null; }
      return u.toString();
    } catch { return null; }
  }, [location.search]);

  /* One place decides where a successful sign-in lands, so the three methods cannot drift. */
  const goAfterAuth = React.useCallback((serverRedirect) => {
    if (nextUrl) { window.location.href = nextUrl; return; }
    if (serverRedirect) { window.location.href = serverRedirect; return; }
    navigate('/Home');
  }, [nextUrl, navigate]);

  useEffect(() => {
    const fromLogout = new URLSearchParams(location.search).has('logout');
    if (fromLogout) return;

    User.me()
      /* Already signed in AND asked to come back somewhere — honour it rather than dropping them
       * on Home, which is what made the connector round trip lose the install half-way. */
      .then(() => { if (nextUrl) { window.location.href = nextUrl; } else { navigate('/Home', { replace: true }); } })
      .catch(() => {});
  }, [navigate, location.search, nextUrl]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const blocked = params.get('blocked');
    if (blocked === 'demarque' || blocked === 'smowltech' || blocked === 'platform') {
      setError(BLOCKED_ACCESS_MESSAGE);
    }
  }, [location.search]);

  const handleGmailAuth = async () => {
    try {
      setIsLoading(true);
      setError('');

      const tempUserId = 'auth_' + Date.now();

      localStorage.setItem('gmail_oauth_user_id', tempUserId);
      localStorage.setItem('gmail_oauth_auth_flow', 'true');

      const response = await fetch(
        `${API_BASE_URL}/api/auth/gmail/start?redirect_uri=${encodeURIComponent(window.location.origin + '/AuthCallback' + (nextUrl ? `?next=${encodeURIComponent(nextUrl)}` : ''))}`,
        { method: 'GET', credentials: 'include' }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('Gmail OAuth start error:', {
          status: response.status,
          statusText: response.statusText,
          error: errorData
        });

        if (response.status === 403) {
          throw new Error('Authentication endpoint is not accessible. Please contact support or use email/password login.');
        }

        throw new Error(errorData.error || `Failed to start Gmail authentication (${response.status})`);
      }

      const data = await response.json();
      if (!data.authUrl) {
        throw new Error('No authorization URL received');
      }

      // Platform auth: full-page redirect in same tab (no popup). AuthCallback will navigate to /Home.
      window.location.href = data.authUrl;
    } catch (err) {
      localStorage.removeItem('gmail_oauth_user_id');
      localStorage.removeItem('gmail_oauth_auth_flow');
      setError(err.message || 'Gmail authentication failed');
      setIsLoading(false);
    }
  };

  const handleOutlookAuth = async () => {
    try {
      setIsLoading(true);
      setError('');

      const tempUserId = 'auth_' + Date.now();

      localStorage.setItem('outlook_oauth_user_id', tempUserId);
      localStorage.setItem('outlook_oauth_auth_flow', 'true');

      const response = await fetch(
        `${API_BASE_URL}/api/auth/outlook/start?redirect_uri=${encodeURIComponent(window.location.origin + '/AuthCallback' + (nextUrl ? `?next=${encodeURIComponent(nextUrl)}` : ''))}`,
        { method: 'GET', credentials: 'include' }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('Outlook OAuth start error:', {
          status: response.status,
          statusText: response.statusText,
          error: errorData
        });

        if (response.status === 403) {
          throw new Error('Authentication endpoint is not accessible. Please contact support or use email/password login.');
        }

        throw new Error(errorData.error || `Failed to start Outlook authentication (${response.status})`);
      }

      const data = await response.json();
      if (!data.authUrl) {
        throw new Error('No authorization URL received');
      }

      // Platform auth: full-page redirect in same tab (no popup). AuthCallback will navigate to /Home.
      window.location.href = data.authUrl;
    } catch (err) {
      localStorage.removeItem('outlook_oauth_user_id');
      localStorage.removeItem('outlook_oauth_auth_flow');
      setError(err.message || 'Outlook authentication failed');
      setIsLoading(false);
    }
  };

  const handleManualAuth = async (e) => {
    e.preventDefault();
    try {
      setIsLoading(true);
      setError('');

      if (isPlatformAccessBlockedEmail(email)) {
        setError(BLOCKED_ACCESS_MESSAGE);
        setIsLoading(false);
        return;
      }

      // Must match backend session tenant, resolved from email domain.
      const tenantId = getTenantFromEmail(email);
      const redirectUrl = location.state?.from || window.location.origin;

      if (isLogin) {
        // Login
        const response = await fetch(`${API_BASE_URL}/api/auth/login`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Tenant-ID': tenantId,
          },
          credentials: 'include',
          body: JSON.stringify({
            email,
            password,
          }),
        });

        if (!response.ok) {
          if (response.status === 403) {
            throw new Error(BLOCKED_ACCESS_MESSAGE);
          }
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.error || 'Login failed');
        }

        const data = await response.json();
        goAfterAuth(data.redirect);
      } else {
        // Signup
        const response = await fetch(`${API_BASE_URL}/api/auth/signup`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Tenant-ID': tenantId,
          },
          credentials: 'include',
          body: JSON.stringify({
            email,
            password,
            display_name: displayName,
            company_name: companyName,
          }),
        });

        if (!response.ok) {
          if (response.status === 403) {
            throw new Error(BLOCKED_ACCESS_MESSAGE);
          }
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.error || 'Signup failed');
        }

        const data = await response.json();
        goAfterAuth(data.redirect);
      }
    } catch (err) {
      setError(err.message || `${isLogin ? 'Login' : 'Signup'} failed`);
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-md"
      >
        <Card className="border-0 shadow-2xl bg-white/95 backdrop-blur-sm">
          <CardHeader className="text-center pb-4">
            <div className="mb-4">
              <img
                src={NEOGTM_LOGO_PNG_URL}
                alt="NEO GTM"
                className="h-8 mx-auto"
              />
            </div>
            <CardTitle className="text-2xl font-bold text-[#0A1A2F]">
              {isLogin ? 'Welcome back' : 'Create your account'}
            </CardTitle>
            <CardDescription className="text-base text-[#5C6B7A] mt-2">
              {isLogin ? 'Sign in to your NEO workspace' : 'Start your journey with NEO GTM'}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md text-sm">
                {error}
              </div>
            )}

            {/* OAuth Buttons */}
            <div className="space-y-3">
              <Button
                onClick={handleGmailAuth}
                disabled={isLoading}
                variant="outline"
                className="w-full h-12 border-2 hover:bg-gray-50 font-semibold"
              >
                <Mail className="w-5 h-5 mr-2" />
                Continue with Gmail
                {isLoading && <Loader2 className="w-4 h-4 ml-2 animate-spin" />}
              </Button>

              <Button
                onClick={handleOutlookAuth}
                disabled={isLoading}
                variant="outline"
                className="w-full h-12 border-2 hover:bg-gray-50 font-semibold"
              >
                <Mail className="w-5 h-5 mr-2" />
                Continue with Outlook
                {isLoading && <Loader2 className="w-4 h-4 ml-2 animate-spin" />}
              </Button>
            </div>

            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-200"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-4 bg-white text-[#5C6B7A]">or</span>
              </div>
            </div>

            {/* Manual Auth Form */}
            <form onSubmit={handleManualAuth} className="space-y-4">
              {!isLogin && (
                <>
                  <div className="space-y-2">
                    <Label htmlFor="displayName">Full Name</Label>
                    <Input
                      id="displayName"
                      type="text"
                      placeholder="John Doe"
                      value={displayName}
                      onChange={(e) => setDisplayName(e.target.value)}
                      required={!isLogin}
                      disabled={isLoading}
                      className="h-12"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="companyName">Company Name (Optional)</Label>
                    <Input
                      id="companyName"
                      type="text"
                      placeholder="Acme Corp"
                      value={companyName}
                      onChange={(e) => setCompanyName(e.target.value)}
                      disabled={isLoading}
                      className="h-12"
                    />
                  </div>
                </>
              )}

              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="you@company.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  disabled={isLoading}
                  className="h-12"
                />
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="password">Password</Label>
                  {isLogin && (
                    <Link to="/ForgotPassword" className="text-sm text-[#00C2C2] hover:underline">
                      Forgot password?
                    </Link>
                  )}
                </div>
                <Input
                  id="password"
                  type="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  disabled={isLoading}
                  className="h-12"
                  minLength={8}
                />
              </div>

              <Button
                type="submit"
                disabled={isLoading}
                className="w-full h-12 bg-[#00C2C2] hover:bg-[#00A3A3] text-white font-semibold"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    {isLogin ? 'Signing in...' : 'Creating account...'}
                  </>
                ) : (
                  <>
                    {isLogin ? 'Sign In' : 'Create Account'}
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </>
                )}
              </Button>
            </form>

            <div className="text-center pt-4">
              <button
                onClick={() => {
                  setIsLogin(!isLogin);
                  setError('');
                  setPassword('');
                }}
                className="text-sm text-[#00C2C2] hover:underline"
                disabled={isLoading}
              >
                {isLogin ? "Don't have an account? Sign up" : 'Already have an account? Sign in'}
              </button>
            </div>

            <div className="pt-4">
              <Button
                onClick={() => navigate('/')}
                variant="ghost"
                className="w-full"
                disabled={isLoading}
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to home
              </Button>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
}

